<script setup lang="ts">
import type Carousel from '@/components/widgets/Carousel.vue';
import type { SpacingProp } from '@/types/Layout';

type Props = {
  spaceBetween?: SpacingProp;
  levels: Array<TLevel>;
  userLevelNumber: number;
};

type Emits = {
  openLevelDetails: [TLevel];
};

const emit = defineEmits<Emits>();

const { isMobile, isLayoutMobileOrTablet, getSpacingMeasurementFromPropList } =
  useLayout();

const {
  spaceBetween = [20, 16, 12],
  levels = [],
  userLevelNumber = 0,
} = defineProps<Props>();

const userLevelIndex = computed<number>(() => {
  return userLevelNumber - 1;
});

const carouselRef = ref<InstanceType<typeof Carousel> | null>(null);

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const isItemsLoading = ref(true);
const errorToLoad = ref(false);

const levelsItems = computed(() => {
  return levels;
});

const hasContentToShow = computed(() => {
  return levelsItems.value && levelsItems.value.length > 0;
});

const isNavigationButtonsVisible = computed<boolean>(() => {
  return levelsItems.value.length > 5 && !isLayoutMobileOrTablet.value;
});

onMounted(() => {
  if (levelsItems.value && levelsItems.value.length) {
    isItemsLoading.value = false;
  }
});
</script>

<template>
  <div
    v-if="isItemsLoading && !errorToLoad"
    class="slideTitle"
  >
    <h4 class="gamificationPageTitle">
      <NuxtIcon
        class="icon"
        name="crown"
      />
      <span>{{ $t('gamification.levels.benefits_of_each_level') }}</span>
    </h4>
  </div>

  <div
    v-else-if="hasContentToShow && !errorToLoad"
    class="slideTitle"
  >
    <h4 class="gamificationPageTitle">
      <NuxtIcon
        class="icon"
        name="crown"
      />
      <span>{{ $t('gamification.levels.benefits_of_each_level') }}</span>
    </h4>

    <div
      v-if="isNavigationButtonsVisible"
      class="relative group flex items-center"
    >
      <NuxtIcon
        class="hover:text-primary cursor-pointer text-texts/60"
        name="arrows/angle_left"
        @click="carouselRef?.scrollToLeft(isMobile ? 134 : 226)"
      />
      <NuxtIcon
        class="hover:text-primary cursor-pointer text-texts/60 ml-3"
        name="arrows/angle_right"
        @click="carouselRef?.scrollToRight(isMobile ? 134 : 226)"
      />
    </div>
  </div>

  <div
    v-if="isItemsLoading && !errorToLoad"
    class="row_imgs loading-indicator"
  >
    <div class="skeleton-loading"></div>
    <div class="skeleton-loading"></div>
    <div class="skeleton-loading"></div>
  </div>

  <div
    v-if="hasContentToShow && !errorToLoad"
    class="row_imgs"
  >
    <LazyWidgetsCarousel
      ref="carouselRef"
      v-model:active-index="userLevelIndex"
      :display="{
        itemsPerRow: { mobile: 3, tablet: 4, desktop: 5 },
        spaceBetweenItems,
      }"
      :item-class="$style.carouselItem"
      :items="levelsItems"
      slider-fade="right"
    >
      <template #item="{ item, index }">
        <div
          :key="item.id"
          class="defaultItemBox"
          @click="emit('openLevelDetails', item)"
        >
          <div
            v-if="userLevelNumber === index + 1"
            class="tagBox current"
          >
            <span>{{ $t('gamification.levels.current_level') }}</span>
          </div>
          <div
            v-else-if="userLevelNumber + 1 === index + 1"
            class="tagBox new"
          >
            <span>{{ $t('gamification.levels.next_level') }}</span>
          </div>
          <div class="imgWrap">
            <img
              :alt="item.name"
              :src="item.image"
            />
          </div>
          <div class="defaultBoxContent gap-4">
            <div class="priceBox inlined noCoins mt-0">
              {{ item.required_points }}
            </div>
            <div class="titleBox titleLevel pt-1">
              <span>{{ item.name }}</span>
            </div>
            <button
              class="flex p-[0.15rem_0.6rem] lg:p-[0.3rem_1rem] gap-[0.25rem] text-[0.75rem] lg:text-[0.95rem] leading-[1rem] lg:leading-[1.2rem] bg-[#0096ff26] bg-gradient-to-b from-[#09173c] to-[#0d1c2b] rounded-[0.2rem] lg:rounded-[0.3rem] border border-[#00c3ff] no-underline"
            >
              <span
                class="text-[0.6rem] lg:text-[0.8rem] font-extrabold text-[#00C3FF]"
                >VER MAIS</span
              >
            </button>
          </div>
        </div>
      </template>
    </LazyWidgetsCarousel>
  </div>
</template>

<style module lang="postcss">
.carouselItem {
  @media (max-width: 768px) {
    @apply min-w-[132px];
  }
}
</style>

<style scoped lang="scss">
.slideTitle {
  @apply flex flex-row items-center mb-1.5;
  .skeleton-loading {
    @apply h-5 w-40 md:mt-1 lg:mb-1;
  }
  .gamificationPageTitle {
    @apply w-auto min-w-min m-0 mr-2;
  }
}
.row_imgs {
  @apply flex gap-2 sm:gap-3 md:gap-4 lg:gap-5 mb-4 sm:mb-5 md:mb-8;
  &.loading-indicator {
    @apply mb-0;
    .skeleton-loading {
      @apply flex-1;
      aspect-ratio: 25/33;
      @media screen and (min-width: 768px) {
        aspect-ratio: 29/8;
      }
    }
  }
}

.moreItemsButton {
  @apply ml-auto py-0 px-2 rounded-full no-underline text-xs flex items-center justify-end gap-1 bg-button-bg/15;

  span {
    @apply font-semibold text-button-bg text-xs text-[.6rem];
  }

  svg {
    @apply hidden;
  }
}
</style>
